import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { BaseContext } from "@app/network-portfolio-management/contexts/BaseContext";
import { useFilterContext } from "@app/network-portfolio-management/contexts/FilterContext";
import {
  GroupByOption,
  NetworkPortfolioManagementProject,
  NetworkPortfolioManagementProjectActionStatus,
  NetworkPortfolioManagementResourceView,
  ProjectGroup,
  SortByOption,
  SortGroupState,
} from "@app/network-portfolio-management/interfaces";
import NetworkPortfolioManagementService from "@app/network-portfolio-management/services/NetworkPortfolioManagementService";
import { useGetGroupedProjects, useGetSortedProjectIds } from "@app/network-portfolio-management/services/npmQueries";
import { resolveSortGroupStateForView, writeStoredSortGroupState } from "@app/network-portfolio-management/sortGroupStorage";
import { UserContext } from "@app/user/UserContext";
import useNPMDashboardStyle from "@app/network-portfolio-management/hooks/useNPMDashboardStyle";
import useNPMColumnVisibility from "@app/network-portfolio-management/hooks/useNPMColumnVisibility";
import { useProjectStatusNotification } from "@app/network-portfolio-management/hooks/useProjectStatusNotification";
import { useBulkProjectActions } from "@app/network-portfolio-management/hooks/useBulkProjectActions";
import { useBulkArchive } from "@app/network-portfolio-management/hooks/useBulkArchive";
import { BulkActionBar } from "@app/network-portfolio-management/components/BulkActionBar";
import { PublishProjectsModal } from "@app/network-portfolio-management/components/PublishProjectsModal";
import { ArchiveProjectsModal } from "@app/network-portfolio-management/components/ArchiveProjectsModal";
import { GroupSection } from "@app/network-portfolio-management/components/GroupSection";
import { PAGE_SIZE, ProjectInfiniteList, renderSkeletons } from "@app/network-portfolio-management/components/ProjectInfiniteList";
import { ProjectTable } from "@app/network-portfolio-management/components/project-table/ProjectTable";
import { ProjectListHeader } from "@app/network-portfolio-management/components/ProjectListHeader";
import { SortGroupControls } from "@app/network-portfolio-management/components/SortGroupControls";
import { useNPMView } from "@app/network-portfolio-management/hooks/useNPMView";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import RoleRestricted from "@app/user/RoleRestricted";
import Roles from "@app/user/roles";

interface ProjectSectionProps {
  projectIds: number[];
}

export const ProjectSection: FC<ProjectSectionProps> = ({ projectIds }) => {
  const userId = useRef(useContext(UserContext).userId).current;

  const { loading: baseLoading } = useContext(BaseContext);
  const view = useNPMView().getCurrentNpmView();
  const isArchived = view === NetworkPortfolioManagementResourceView.Archived;
  const { activeFilters } = useFilterContext();
  const { projectAction } = activeFilters;
  // Bulk selection (per-project checkboxes + the Bulk Action bar) is only offered once a Project Action
  // filter is active — there's nothing to bulk-act on until the list is narrowed to an eligibility cohort.
  const selectionEnabled = !isArchived && projectAction !== null;

  const [sortGroupState, setSortGroupState] = useState<SortGroupState>(() => resolveSortGroupStateForView(view));

  // Re-resolve the instant the view changes — React's "adjust state while rendering" pattern. The resolve is
  // synchronous (no stale-data render, no useEffect), and sortGroupState stays the single source the queries
  // below read. Each view remembers only its own last selection — switching never carries a selection across
  // views, valid or not.
  const prevView = useRef(view);
  if (prevView.current !== view) {
    prevView.current = view;
    setSortGroupState(resolveSortGroupStateForView(view));
  }

  // Persists on every real change (SortGroupControls' onChange handlers). Writes before setting state
  // — matching useNPMDashboardStyle above — so the storage write stays outside setSortGroupState's
  // updater, which React requires to be pure. The render-phase resolve above calls setSortGroupState
  // directly, not this updater, so it's a read with nothing written back.
  const updateSortGroupState = useCallback(
    (state: SortGroupState) => {
      writeStoredSortGroupState(view, state);
      setSortGroupState(state);
    },
    [view],
  );

  // groupingEnabled uses !== None so any future Group By dimension works automatically.
  const isGrouped = sortGroupState.groupBy !== GroupByOption.None;
  // AlphabeticalAZ is already A→Z from the base project-resources list; no /sorted call needed.
  // All other sorts (ZA, LastUpdated, AdequacyScore, GapCount) need the /sorted endpoint to
  // get the correct global order; IDs are then sliced client-side per page.
  const isDataDrivenSort = sortGroupState.sortBy !== SortByOption.AlphabeticalAZ;

  const [viewMode, setViewMode] = useNPMDashboardStyle();
  const [columnVisibility, setColumnVisibility] = useNPMColumnVisibility();
  const [selectedProjectIds, setSelectedProjectIds] = useState<Set<number>>(new Set());
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  // Loaded rows from the (ungrouped) infinite list, used to resolve selected project names for the publish modal.
  const [loadedRows, setLoadedRows] = useState<NetworkPortfolioManagementProject[]>([]);
  const service = useRef(new NetworkPortfolioManagementService()).current;
  useProjectStatusNotification(projectIds, service, sortGroupState.sortBy);
  const totalCount = projectIds.length;

  const { data: groupsData, error: groupsError, isLoading: groupsIsLoading } = useGetGroupedProjects(
    userId, projectIds, sortGroupState.groupBy, sortGroupState.sortBy, isGrouped,
  );

  // Sorted IDs query: only active for data-driven sorts in the ungrouped view. The backend
  // returns the full ID list in sort order once; the client slices it per page.
  const { data: sortedProjectIds, error: sortedIdsError, isLoading: sortedIdsLoading } = useGetSortedProjectIds(
    userId, projectIds, sortGroupState.sortBy, isDataDrivenSort && !isGrouped,
  );

  // Gate on isGrouped: React Query retains the last partitions after `enabled` flips to false,
  // so without this, switching Group By back to None would expose stale groups.
  const groups: ProjectGroup[] = isGrouped ? (groupsData ?? []) : [];

  const clearSelection = useCallback(() => setSelectedProjectIds(new Set()), []);
  const { isDispatching, dispatch } = useBulkProjectActions(clearSelection);
  const { isArchiving, archive } = useBulkArchive(clearSelection);

  // Select All selects the entire filtered cohort (all projectIds), not just the rows currently
  // rendered — bulk Run/Publish must act on every eligible project even though only the first page
  // (or expanded group) is loaded on screen.
  const handleSelectAllChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedProjectIds(e.target.checked ? new Set(projectIds) : new Set());
  }, [projectIds]);

  const handleToggleProject = useCallback((id: number) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // A group header checkbox adds or removes only that group's ids, so entries belonging to other groups
  // are never disturbed as a side effect. Like Select All, this covers the group's entire partition —
  // including projects on pages the group hasn't lazily loaded yet.
  const handleToggleGroup = useCallback((groupProjectIds: number[], checked: boolean) => {
    setSelectedProjectIds((prev) => {
      const next = new Set(prev);
      groupProjectIds.forEach((id) => (checked ? next.add(id) : next.delete(id)));
      return next;
    });
  }, []);

  // Publish opens a confirmation modal (project list + optional comment) before dispatching; Run
  // dispatches directly — it queues immediately and takes no comment.
  const handleDispatch = useCallback((kind: NetworkPortfolioManagementProjectActionStatus) => {
    if (kind === NetworkPortfolioManagementProjectActionStatus.Publish) {
      setPublishModalOpen(true);
      return;
    }
    dispatch(NetworkPortfolioManagementProjectActionStatus.Run, [...selectedProjectIds]);
  }, [dispatch, selectedProjectIds]);

  const handleConfirmPublish = useCallback((comment: string) => {
    dispatch(NetworkPortfolioManagementProjectActionStatus.Publish, [...selectedProjectIds], comment);
    setPublishModalOpen(false);
  }, [dispatch, selectedProjectIds]);

  // Archive is destructive and irreversible, so it always opens a confirmation modal first. On confirm we always
  // close the modal; archive() clears the selection itself only when at least one project was archived.
  const handleConfirmArchive = useCallback(async () => {
    await archive([...selectedProjectIds]);
    setArchiveModalOpen(false);
  }, [archive, selectedProjectIds]);

  const handleOpenArchiveModal = useCallback(() => setArchiveModalOpen(true), []);

  // Checked / indeterminate state for each group header, derived from the same flat selectedProjectIds
  // Set the global Select All reads. Deriving (rather than storing per-group state) is what keeps a group
  // header in sync no matter how the selection changed — its own checkbox, an individual project
  // checkbox, or the global Select All. Set lookups keep this O(total projects) per pass.
  const groupSelectionState = useMemo(() => {
    const state = new Map<string, { all: boolean; some: boolean }>();
    groups.forEach((group) => {
      const selectedInGroup = group.projectIds.reduce((n, id) => (selectedProjectIds.has(id) ? n + 1 : n), 0);
      state.set(group.key, {
        all: group.projectIds.length > 0 && selectedInGroup === group.projectIds.length,
        some: selectedInGroup > 0 && selectedInGroup < group.projectIds.length,
      });
    });
    return state;
  }, [groups, selectedProjectIds]);

  // Names for the publish modal's list, resolved from the loaded rows. Select All can span unloaded
  // pages, so the modal shows the resolvable names plus an "…and N more" line for the remainder.
  const selectedProjects = useMemo(
    () => loadedRows.filter((r) => selectedProjectIds.has(r.projectId)).map((r) => ({ name: r.name, projectId: r.projectId })),
    [loadedRows, selectedProjectIds],
  );

  // Any change to the filtered cohort clears the selection. This is deliberate: a selection is scoped to
  // the cohort the user is acting on, and silently carrying hidden, off-cohort selections into a bulk
  // Run/Publish/Archive would act on projects the user can no longer see. Sort By and Group By changes do
  // not alter projectIds, so selections correctly survive those.
  useEffect(() => {
    setSelectedProjectIds(new Set());
  }, [projectIds]);

  // Changing the Project Action filter clears any in-progress selection — selections only make
  // sense relative to the cohort the user is currently acting on.
  useEffect(() => {
    setSelectedProjectIds(new Set());
  }, [projectAction]);

  const sortGroupSlot = (
    <SortGroupControls
      sortGroupState={sortGroupState}
      setSortGroupState={updateSortGroupState}
      archivedView={isArchived}
    />
  );

  if (baseLoading || (isGrouped && groupsIsLoading) || (!isGrouped && isDataDrivenSort && sortedIdsLoading)) {
    // totalCount is 0 while BaseContext is still resolving, so only the header renders briefly.
    // Once ids arrive, count reflects the real total (capped at PAGE_SIZE) and skeletons fill in.
    const count = Math.min(PAGE_SIZE, totalCount);
    return (
      <>
        <ProjectListHeader
          columnVisibility={columnVisibility}
          onColumnVisibilityChange={setColumnVisibility}
          sortGroupSlot={sortGroupSlot}
          viewMode={viewMode}
          onViewModeChange={setViewMode}
        />
        {viewMode === "rows" ? (
          <ProjectTable
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onToggleSelect={handleToggleProject}
            projectIds={[]}
            selectedProjectIds={selectedProjectIds}
            selectionEnabled={selectionEnabled}
            skeletonRowCount={count}
            sortBy={sortGroupState.sortBy}
          />
        ) : (
          <div className="network-portfolio-management-project-cards">{renderSkeletons(count)}</div>
        )}
      </>
    );
  }

  if (totalCount === 0) {
    return <h3 className="text-muted no-content-text">No Project Data to Display</h3>;
  }

  return (
    <>
      <ProjectListHeader
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
        sortGroupSlot={sortGroupSlot}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />

      {selectionEnabled && (
        <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN]}>
          <BulkActionBar
            filter={projectAction}
            selectedCount={selectedProjectIds.size}
            allSelected={totalCount > 0 && selectedProjectIds.size === totalCount}
            someSelected={selectedProjectIds.size > 0 && selectedProjectIds.size < totalCount}
            onSelectAllChange={handleSelectAllChange}
            isDispatching={isDispatching}
            onDispatch={handleDispatch}
            isArchiving={isArchiving}
            onArchive={handleOpenArchiveModal}
          />
        </RoleRestricted>
      )}

      <PublishProjectsModal
        open={publishModalOpen}
        projects={selectedProjects}
        totalCount={selectedProjectIds.size}
        isPublishing={isDispatching}
        onCancel={() => setPublishModalOpen(false)}
        onPublish={handleConfirmPublish}
      />

      <ArchiveProjectsModal
        open={archiveModalOpen}
        count={selectedProjectIds.size}
        isArchiving={isArchiving}
        onCancel={() => setArchiveModalOpen(false)}
        onConfirm={handleConfirmArchive}
      />

      {!!groupsError || (!isGrouped && !!sortedIdsError) ? (
        <QESAlert type="danger" persist={true}>
          Failed to load, please try again. Contact Quest Client Services for assistance if error persists.
        </QESAlert>
      ) : isGrouped ? (
        groups.map((group) => (
          <GroupSection
            key={group.key}
            columnVisibility={columnVisibility}
            groupName={group.label}
            count={group.projectIds.length}
            projectIds={group.projectIds}
            sortBy={sortGroupState.sortBy}
            onColumnVisibilityChange={setColumnVisibility}
            onToggleSelect={handleToggleProject}
            onToggleGroup={handleToggleGroup}
            groupAllSelected={!!groupSelectionState.get(group.key)?.all}
            groupSomeSelected={!!groupSelectionState.get(group.key)?.some}
            selectedProjectIds={selectedProjectIds}
            selectionEnabled={selectionEnabled}
            viewMode={viewMode}
            isSorted={true}
          />
        ))
      ) : viewMode === "rows" ? (
        <ProjectTable
          columnVisibility={columnVisibility}
          projectIds={isDataDrivenSort ? (sortedProjectIds ?? projectIds) : projectIds}
          sortBy={sortGroupState.sortBy}
          selectedProjectIds={selectedProjectIds}
          onColumnVisibilityChange={setColumnVisibility}
          onToggleSelect={handleToggleProject}
          selectionEnabled={selectionEnabled}
          onLoadedRowsChange={setLoadedRows}
          isSorted={isDataDrivenSort}
        />
      ) : (
        <ProjectInfiniteList
          projectIds={isDataDrivenSort ? (sortedProjectIds ?? projectIds) : projectIds}
          sortBy={sortGroupState.sortBy}
          selectedProjectIds={selectedProjectIds}
          onToggleSelect={handleToggleProject}
          selectionEnabled={selectionEnabled}
          onLoadedRowsChange={setLoadedRows}
          isSorted={isDataDrivenSort}
        />
      )}
    </>
  );
};
